import React from "react";
import { LOGO_URL } from "@/components/Logo";
import { money, formatDate } from "@/lib/api";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function jobHours(job) {
  const s = String(job.start_time || "").match(/^(\d{1,2}):(\d{2})$/);
  const e = String(job.end_time || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!s || !e) return 0;
  const mins = (+e[1] * 60 + +e[2]) - (+s[1] * 60 + +s[2]);
  return mins > 0 ? mins / 60 : 0;
}

function jobPay(job, employee) {
  const rate = Number(employee.pay_rate || 0);
  if (employee.pay_type === "Per Job") return rate;
  if (employee.pay_type === "Hourly") return jobHours(job) * rate;
  return 0;
}

const fullAddress = (j) =>
  [j.address, [j.city, j.state, j.zip].filter(Boolean).join(", ")].filter(Boolean).join(", ");

// employee, jobs (already filtered), payroll (already filtered), rangeLabel, company settings
export default function PayrollReport({ employee, jobs = [], payroll = [], rangeLabel, company = {} }) {
  if (!employee) return null;
  const logo = company.logo_url || LOGO_URL;

  const totalHours = jobs.reduce((a, j) => a + jobHours(j), 0);
  const totalJobPay = jobs.reduce((a, j) => a + jobPay(j, employee), 0);
  const hourlyPay = employee.pay_type === "Hourly" ? totalJobPay : 0;
  const fixedPay = employee.pay_type === "Per Job" ? totalJobPay : 0;
  const jobsCompleted = jobs.filter((j) => j.status === "Completed").length;
  const avgPerJob = jobs.length ? totalJobPay / jobs.length : 0;

  const netOf = (r) => (r.net_pay != null ? Number(r.net_pay) : Number(r.gross_pay || 0) - Number(r.deductions || 0));
  const earned = payroll.reduce((a, r) => a + netOf(r), 0);
  const paid = payroll.filter((r) => (r.status || "Pending") === "Paid").reduce((a, r) => a + netOf(r), 0);
  const balance = earned - paid;

  const now = new Date();
  const printedOn = `${MONTHS[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;

  const summary = [
    ["Total Hours Worked", `${totalHours.toFixed(1)} hrs`],
    ["Jobs Completed", jobsCompleted],
    ["Total Hourly Pay", money(hourlyPay)],
    ["Total Fixed Job Pay", money(fixedPay)],
    ["Total Payroll Earned", money(earned)],
    ["Total Payroll Paid", money(paid)],
    ["Remaining Balance", money(balance)],
    ["Average Pay Per Job", money(avgPerJob)],
  ];

  return (
    <div
      id="payroll-print-area"
      className="bg-white mx-auto"
      style={{ width: "8.5in", maxWidth: "100%", padding: "0.5in", color: "#121212", boxSizing: "border-box" }}
      data-testid="payroll-report"
    >
      {/* Header */}
      <div className="flex justify-between items-start pb-5 border-b-2" style={{ borderColor: "#5A189A" }}>
        <div className="flex items-center gap-3">
          <img src={logo} alt="logo" style={{ width: 72, height: 72 }} className="rounded-full object-cover" />
          <div>
            <div className="font-display text-xl font-bold" style={{ color: "#5A189A" }}>
              {company.company_name || "Magic Touch Professional Cleaning"}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "#52525B" }}>
              {company.phone}{company.phone && company.email ? " · " : ""}{company.email}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-bold tracking-tight" style={{ color: "#D4AF37" }}>
            PAYROLL STATEMENT
          </div>
          <div className="text-xs mt-1" style={{ color: "#52525B" }}>Printed on: {printedOn}</div>
        </div>
      </div>

      {/* Employee + period */}
      <div className="flex justify-between mt-5">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: "#D4AF37" }}>Employee</div>
          <div className="font-semibold text-base" style={{ color: "#121212" }}>{employee.name}</div>
          <div className="text-xs mt-0.5" style={{ color: "#52525B" }}>
            {employee.role}{employee.crew ? ` · ${employee.crew}` : ""}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: "#D4AF37" }}>Pay Period</div>
          <div className="font-semibold text-sm" style={{ color: "#121212" }}>{rangeLabel}</div>
        </div>
      </div>

      {/* Summary grid */}
      <div className="mt-5" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
        {summary.map(([label, value]) => (
          <div key={label} style={{ background: "#faf8fd", border: "1px solid #efeaf6", borderRadius: "10px", padding: "10px 12px" }}>
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#9b8bb5" }}>{label}</div>
            <div className="font-bold mt-1" style={{ color: "#5A189A", fontSize: "15px" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Detail table */}
      <div className="text-[10px] uppercase tracking-wider font-semibold mt-6 mb-2" style={{ color: "#D4AF37" }}>Job Detail</div>
      <table className="w-full" style={{ borderCollapse: "collapse", fontSize: "11px" }}>
        <thead>
          <tr style={{ background: "#5A189A", color: "#fff" }}>
            <th style={{ textAlign: "left", padding: "7px 8px" }}>Date</th>
            <th style={{ textAlign: "left", padding: "7px 8px" }}>Client</th>
            <th style={{ textAlign: "left", padding: "7px 8px" }}>Service</th>
            <th style={{ textAlign: "left", padding: "7px 8px" }}>Property Address</th>
            <th style={{ textAlign: "left", padding: "7px 8px" }}>Type</th>
            <th style={{ textAlign: "center", padding: "7px 8px" }}>Hours</th>
            <th style={{ textAlign: "right", padding: "7px 8px" }}>Rate</th>
            <th style={{ textAlign: "right", padding: "7px 8px" }}>Pay</th>
            <th style={{ textAlign: "left", padding: "7px 8px" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {jobs.length === 0 ? (
            <tr><td colSpan={9} style={{ padding: "16px", textAlign: "center", color: "#9b8bb5" }}>No jobs in this period.</td></tr>
          ) : jobs.map((j) => (
            <tr key={j.id} style={{ borderBottom: "1px solid #efeaf6" }}>
              <td style={{ padding: "6px 8px" }}>{formatDate(j.date)}</td>
              <td style={{ padding: "6px 8px" }}>{j.customer_name}</td>
              <td style={{ padding: "6px 8px" }}>{j.service}</td>
              <td style={{ padding: "6px 8px", color: "#52525B" }}>{fullAddress(j) || "-"}</td>
              <td style={{ padding: "6px 8px" }}>{employee.pay_type}</td>
              <td style={{ padding: "6px 8px", textAlign: "center" }}>{jobHours(j).toFixed(1)}</td>
              <td style={{ padding: "6px 8px", textAlign: "right" }}>{money(employee.pay_rate)}</td>
              <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600 }}>{money(jobPay(j, employee))}</td>
              <td style={{ padding: "6px 8px" }}>{j.status}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="text-center text-[10px] mt-8 pt-4 border-t" style={{ borderColor: "#efeaf6", color: "#9b8bb5" }}>
        {company.company_name || "Magic Touch Professional Cleaning"} · Payroll Statement · {rangeLabel}
      </div>
    </div>
  );
}
